import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAdminToken, clearAdminToken, adminFetchInit } from '../utils/adminToken';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminToken = async () => {
      const legacy = getAdminToken();
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      // Cookie-only sessions have no legacy token; still verify on any admin or streamer back-office URL.
      const onAdminPath = pathname.startsWith('/admin');
      const onStreamerBackOfficePath = pathname.startsWith('/streamer/');
      if (!legacy && !onAdminPath && !onStreamerBackOfficePath) {
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
          method: 'POST',
          body: JSON.stringify({
            action: 'verify',
            token: legacy || undefined
          })
        }));

        const data = await response.json();

        if (response.ok && data.success) {
          setAdmin(data.admin);
        } else {
          clearAdminToken();
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        clearAdminToken();
      }
      setIsLoading(false);
    };

    checkAdminToken();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'login',
          email,
          password
        })
      }));

      const data = await response.json();

      if (response.ok && data.success) {
        clearAdminToken();
        setAdmin(data.admin);
        return { success: true, admin: data.admin };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = async () => {
    try {
      await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ action: 'logout' })
      }));
    } catch (e) {
      console.error('Admin logout request failed:', e);
    }
    clearAdminToken();
    setAdmin(null);
  };

  const value = {
    admin,
    setAdmin,
    isLoading,
    login,
    logout,
    isAuthenticated: !!admin
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
